import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import logo       from '../../assets/logo.png';
import dhanLogo   from '../../assets/brokers/dhan.png';
import zerodhaLogo from '../../assets/brokers/zerodha.png';

/*
  WelcomePage — Industry Grade Onboarding
  ────────────────────────────────────────
  - Real logos (DeltaDesk + broker logos)
  - Username from Cognito (name / given_name / email prefix)
  - Fully responsive (mobile + desktop)
  - Step 1: Select broker(s) → Request Access
  - Step 2: Submitted state → CTA to Strategy page
*/

const C = {
  bg:      '#060912',
  surface: '#0c1120',
  surface2:'#111827',
  border:  '#1e2d45',
  border2: '#2a3f5f',
  green:   '#00e5a0',
  red:     '#ff4d6d',
  amber:   '#f5a623',
  blue:    '#3d8ef0',
  muted:   '#5a6a82',
  muted2:  '#8899b0',
};
const mono = "'DM Mono', monospace";
const sans = "'Outfit', sans-serif";

const BROKERS = [
  {
    id:          'dhan',
    name:        'Dhan',
    logo:        dhanLogo,
    description: 'Modern trading platform with fast order execution and comprehensive API access for algorithmic strategies.',
    color:       '#f5a623',
    features:    ['Options & Futures', 'Dhan HQ API', 'Real-time WebSocket data'],
  },
  {
    id:          'kite',
    name:        'Zerodha Kite',
    logo:        zerodhaLogo,
    description: 'India\'s largest stockbroker with the robust Kite Connect API powering thousands of algo traders.',
    color:       '#3d8ef0',
    features:    ['Options & Futures', 'Kite Connect API', 'GTT & bracket orders'],
  },
];

/* ── derive display name from Cognito user object ── */
function getDisplayName(user) {
  return (
    user?.name        ||
    user?.given_name  ||
    user?.username    ||
    user?.email?.split('@')[0] ||
    'Trader'
  );
}

/* ══════════════════════════════════════════════
   BROKER CARD
══════════════════════════════════════════════ */
function BrokerCard({ broker, selected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        position:    'relative',
        background:  selected
          ? `linear-gradient(145deg, ${broker.color}14, ${broker.color}06)`
          : C.surface2,
        border:      `1.5px solid ${selected ? broker.color : C.border}`,
        borderRadius: 16,
        padding:     '20px',
        cursor:      'pointer',
        transition:  'all 0.2s ease',
        userSelect:  'none',
      }}
    >
      {/* top accent bar */}
      {selected && (
        <div style={{
          position:   'absolute', top: 0, left: 0, right: 0,
          height:     2, borderRadius: '16px 16px 0 0',
          background: `linear-gradient(90deg, ${broker.color}, ${broker.color}40)`,
        }} />
      )}

      {/* header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* broker logo */}
          <div style={{
            width: 44, height: 44, borderRadius: 12, overflow: 'hidden',
            background:  `${broker.color}15`,
            border:      `1px solid ${broker.color}30`,
            display:     'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink:  0,
          }}>
            <img
              src={broker.logo}
              alt={broker.name}
              style={{ width: 28, height: 28, objectFit: 'contain' }}
            />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', fontFamily: sans, lineHeight: 1.2 }}>
              {broker.name}
            </div>
            <div style={{ fontSize: 10, color: broker.color, fontFamily: mono, fontWeight: 700, letterSpacing: '0.5px', marginTop: 2 }}>
              SUPPORTED
            </div>
          </div>
        </div>

        {/* checkbox */}
        <div style={{
          width:        24, height: 24, borderRadius: '50%', flexShrink: 0,
          background:   selected ? broker.color : 'transparent',
          border:       `2px solid ${selected ? broker.color : C.border2}`,
          display:      'flex', alignItems: 'center', justifyContent: 'center',
          transition:   'all 0.2s',
          fontSize:     12, fontWeight: 800,
          color:        C.bg,
        }}>
          {selected && '✓'}
        </div>
      </div>

      {/* description */}
      <p style={{
        fontSize: 12, color: C.muted2, fontFamily: sans,
        lineHeight: 1.65, margin: '0 0 14px',
      }}>
        {broker.description}
      </p>

      {/* feature pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {broker.features.map(f => (
          <span key={f} style={{
            fontSize:     10, fontFamily: mono,
            color:        selected ? broker.color : C.muted,
            background:   selected ? `${broker.color}12` : 'rgba(255,255,255,0.04)',
            border:       `1px solid ${selected ? `${broker.color}30` : C.border}`,
            borderRadius: 20, padding:    '3px 10px',
            transition:   'all 0.2s',
          }}>
            {f}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   STEP INDICATOR
══════════════════════════════════════════════ */
function Steps({ current }) {
  const steps = ['Select Broker', 'Request Sent', 'Admin Approves', 'Start Trading'];
  return (
    <div style={{
      display:        'flex', alignItems: 'flex-start',
      justifyContent: 'center', gap: 0,
      width:          '100%', maxWidth: 480,
      marginInline:   'auto', marginBottom: 32,
    }}>
      {steps.map((label, i) => {
        const n    = i + 1;
        const done = current > n;
        const active = current === n;
        return (
          <React.Fragment key={label}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: 'none' }}>
              <div style={{
                width:      28, height: 28, borderRadius: '50%',
                display:    'flex', alignItems: 'center', justifyContent: 'center',
                fontSize:   11, fontWeight: 700, fontFamily: mono,
                background: done ? C.green : active ? 'rgba(0,229,160,0.12)' : 'rgba(255,255,255,0.04)',
                border:     `1.5px solid ${done ? C.green : active ? 'rgba(0,229,160,0.5)' : C.border}`,
                color:      done ? C.bg : active ? C.green : C.muted,
                transition: 'all 0.3s',
              }}>
                {done ? '✓' : n}
              </div>
              <span style={{
                fontSize:   9, fontFamily: mono, whiteSpace: 'nowrap',
                color:      done || active ? C.muted2 : C.muted,
                fontWeight: active ? 700 : 400,
              }}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex:       1, height: 1.5, margin: '13px 4px 0',
                minWidth:   20,
                background: done
                  ? `linear-gradient(90deg, ${C.green}, ${C.green}60)`
                  : C.border,
                transition: 'background 0.4s',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════ */
export default function WelcomePage() {
  const navigate         = useNavigate();
  const { user }         = useAuth();
  const [step, setStep]  = useState(1);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error,   setError]     = useState(null);
  const [mounted, setMounted]   = useState(false);

  const displayName      = getDisplayName(user);
  const alreadyRequested = user?.access_requested === true;

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    if (alreadyRequested) setStep(2);
    return () => clearTimeout(t);
  }, [alreadyRequested]);

  const toggleBroker = id => {
    setSelected(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selected.length) {
      setError('Select at least one broker to continue.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { default: client } = await import('../../api/client');
      await client.post('admin/onboarding/request', { brokers: selected });
      setStep(2);
    } catch (e) {
      setError(
        e?.response?.data?.detail ??
        e?.response?.data?.message ??
        'Something went wrong. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  /* ── styles ── */
  const cardStyle = {
    background:   C.surface,
    border:       `1px solid ${C.border}`,
    borderRadius: 20,
    padding:      'clamp(20px, 4vw, 32px)',
    position:     'relative',
    overflow:     'hidden',
  };

  return (
    <>
      <style>{`
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        * { box-sizing: border-box; }
        body { margin: 0; background: ${C.bg}; }

        .welcome-grid {
          background-image:
            linear-gradient(rgba(0,229,160,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,229,160,0.04) 1px, transparent 1px);
          background-size: 36px 36px;
        }

        @media (max-width: 600px) {
          .broker-grid { grid-template-columns: 1fr !important; }
          .step-label  { display: none !important; }
          .stat-grid   { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <div
        className="welcome-grid"
        style={{
          minHeight:      '100vh',
          background:     C.bg,
          display:        'flex',
          flexDirection:  'column',
          alignItems:     'center',
          justifyContent: 'flex-start',
          padding:        'clamp(24px, 5vw, 60px) clamp(16px, 4vw, 24px)',
          position:       'relative',
          overflow:       'hidden',
        }}
      >

        {/* ── Background orbs ── */}
        <div style={{ position:'fixed', inset:0, pointerEvents:'none', zIndex:0 }}>
          <div style={{ position:'absolute', width:600, height:600, borderRadius:'50%', background:'#00e5a0', opacity:0.05, filter:'blur(100px)', top:-200, left:-100 }} />
          <div style={{ position:'absolute', width:500, height:500, borderRadius:'50%', background:'#3d8ef0', opacity:0.05, filter:'blur(100px)', bottom:-150, right:-80 }} />
        </div>

        {/* ── Main content ── */}
        <div style={{
          position:   'relative', zIndex: 1,
          width:      '100%', maxWidth: 660,
          animation:  mounted ? 'fadeUp 0.5s ease forwards' : 'none',
          opacity:    mounted ? 1 : 0,
        }}>

          {/* ── Navbar strip ── */}
          <div style={{
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'space-between',
            marginBottom:   'clamp(32px, 5vw, 52px)',
          }}>
            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src={logo} alt="DeltaDesk" style={{ width: 32, height: 32, borderRadius: 8, objectFit:'contain' }} />
              <span style={{ fontSize: 17, fontWeight: 800, color: '#fff', fontFamily: sans, letterSpacing: '-0.3px' }}>
                DeltaDesk
              </span>
            </div>

            {/* User chip */}
            <div style={{
              display:      'flex', alignItems: 'center', gap: 8,
              background:   C.surface,
              border:       `1px solid ${C.border}`,
              borderRadius: 20, padding:     '5px 12px 5px 8px',
            }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: `linear-gradient(135deg, ${C.green}40, ${C.blue}30)`,
                border: `1px solid ${C.green}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 800, color: C.green, fontFamily: mono,
              }}>
                {displayName[0]?.toUpperCase()}
              </div>
              <span style={{ fontSize: 12, color: C.muted2, fontFamily: mono }}>
                {displayName}
              </span>
            </div>
          </div>

          {/* ── Hero text ── */}
          <div style={{ textAlign: 'center', marginBottom: 'clamp(24px, 4vw, 40px)' }}>
            {/* badge */}
            <div style={{
              display:       'inline-flex', alignItems: 'center', gap: 6,
              background:    'rgba(0,229,160,0.08)',
              border:        '1px solid rgba(0,229,160,0.2)',
              borderRadius:  20, padding: '4px 14px',
              fontSize:      10, fontWeight: 700, fontFamily: mono,
              color:         C.green, letterSpacing: '1px', textTransform: 'uppercase',
              marginBottom:  16,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, display: 'inline-block', animation: 'pulse 2s infinite' }} />
              {step === 1 ? 'Account Setup' : 'Request Submitted'}
            </div>

            <h1 style={{
              fontSize:     'clamp(24px, 5vw, 36px)',
              fontWeight:   800, color:  '#fff',
              fontFamily:   sans, letterSpacing: '-1px',
              margin:       '0 0 12px', lineHeight: 1.15,
            }}>
              {step === 1 ? (
                <>Welcome, {displayName} 👋</>
              ) : (
                <>You're on the list ✅</>
              )}
            </h1>

            <p style={{
              fontSize:    'clamp(13px, 2vw, 15px)',
              color:       C.muted2, fontFamily: sans,
              lineHeight:  1.7, margin: 0,
              maxWidth:    460, marginInline: 'auto',
            }}>
              {step === 1
                ? 'Select your broker(s) and request access. Our team will configure your trading account within 24 hours.'
                : 'Your request has been sent to the admin team. You\'ll be notified once your account is ready to trade.'
              }
            </p>
          </div>

          {/* ── Step indicator ── */}
          <Steps current={step} />

          {/* ════════════════════════════
              STEP 1 — Select broker
          ════════════════════════════ */}
          {step === 1 && (
            <div style={cardStyle}>
              {/* top gradient line */}
              <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:`linear-gradient(90deg, ${C.green}, ${C.blue}80, transparent)` }} />

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', fontFamily: sans, marginBottom: 4 }}>
                  Choose your broker(s)
                </div>
                <div style={{ fontSize: 12, color: C.muted, fontFamily: mono }}>
                  Select the broker(s) you'd like to connect for automated trading.
                </div>
              </div>

              {/* broker cards */}
              <div
                className="broker-grid"
                style={{
                  display:               'grid',
                  gridTemplateColumns:   '1fr 1fr',
                  gap:                   14,
                  marginBottom:          20,
                }}
              >
                {BROKERS.map(b => (
                  <BrokerCard
                    key={b.id}
                    broker={b}
                    selected={selected.includes(b.id)}
                    onClick={() => toggleBroker(b.id)}
                  />
                ))}
              </div>

              {/* selection summary */}
              {selected.length > 0 && (
                <div style={{
                  display:      'flex', alignItems: 'center', gap: 10,
                  background:   'rgba(0,229,160,0.06)',
                  border:       '1px solid rgba(0,229,160,0.15)',
                  borderRadius: 10, padding: '10px 14px', marginBottom: 16,
                  fontSize: 12, fontFamily: mono, color: C.muted2,
                }}>
                  <span style={{ color: C.green }}>✓</span>
                  <span>
                    <span style={{ color: C.green, fontWeight: 700 }}>
                      {selected.map(id => BROKERS.find(b => b.id === id)?.name).join(' + ')}
                    </span>
                    {' '}selected · admin will be notified via email
                  </span>
                </div>
              )}

              {/* error */}
              {error && (
                <div style={{
                  background: 'rgba(255,77,109,0.08)', border: '1px solid rgba(255,77,109,0.2)',
                  borderRadius: 10, padding: '10px 14px', marginBottom: 14,
                  fontSize: 12, color: C.red, fontFamily: mono,
                }}>
                  ⚠ {error}
                </div>
              )}

              {/* CTA button */}
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  width:        '100%', padding: '14px 20px',
                  background:   loading
                    ? 'rgba(0,229,160,0.08)'
                    : `linear-gradient(135deg, ${C.green} 0%, #00c890 100%)`,
                  border:       `1px solid ${loading ? C.border : 'transparent'}`,
                  borderRadius: 12,
                  color:        loading ? C.muted : C.bg,
                  fontSize:     14, fontWeight: 800, fontFamily: sans,
                  cursor:       loading ? 'not-allowed' : 'pointer',
                  display:      'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition:   'opacity 0.2s',
                  letterSpacing:'-0.2px',
                }}
              >
                {loading ? (
                  <>
                    <div style={{ width:16, height:16, border:`2px solid ${C.border}`, borderTop:`2px solid ${C.green}`, borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
                    Sending request…
                  </>
                ) : (
                  <>Request Broker Access →</>
                )}
              </button>

              <p style={{ textAlign:'center', fontSize:11, color:C.muted, fontFamily:mono, marginTop:14, marginBottom:0, lineHeight:1.6 }}>
                Admin will be notified via email. Your credentials are never shared.
              </p>
            </div>
          )}

          {/* ════════════════════════════
              STEP 2 — Submitted
          ════════════════════════════ */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* status card */}
              <div style={{ ...cardStyle }}>
                <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:`linear-gradient(90deg, ${C.green}, ${C.blue}80, transparent)` }} />

                <div style={{ display:'flex', alignItems:'flex-start', gap:16 }}>
                  <div style={{
                    width:48, height:48, borderRadius:14, flexShrink:0,
                    background:'rgba(0,229,160,0.08)', border:'1px solid rgba(0,229,160,0.2)',
                    display:'flex', alignItems:'center', justifyContent:'center', fontSize:22,
                  }}>
                    📬
                  </div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:'#fff', fontFamily:sans, marginBottom:4 }}>
                      Admin team notified
                    </div>
                    <div style={{ fontSize:12, color:C.muted2, fontFamily:mono, lineHeight:1.65 }}>
                      Your access request has been submitted. Typical approval time is within 24 hours on business days.
                    </div>
                  </div>
                </div>
              </div>

              {/* what happens next */}
              <div style={cardStyle}>
                <div style={{ fontSize:10, fontWeight:700, color:C.muted, fontFamily:mono, letterSpacing:'1px', textTransform:'uppercase', marginBottom:14 }}>
                  What happens next
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {[
                    { icon:'📧', step:'1', title:'Email sent to admin',  desc:'Admin receives your request with broker preferences and account details.' },
                    { icon:'⚙️', step:'2', title:'Account configured',   desc:'Admin connects your broker API keys and configures your trading strategy.' },
                    { icon:'✅', step:'3', title:'You\'re live',         desc:'Once approved, refresh this page. Your dashboard and orders will be ready.' },
                  ].map(({ icon, step: s, title, desc }) => (
                    <div key={title} style={{
                      display:'flex', alignItems:'flex-start', gap:12,
                      padding:'12px 14px', background:C.surface2,
                      border:`1px solid ${C.border}`, borderRadius:12,
                    }}>
                      <div style={{
                        width:32, height:32, borderRadius:10, flexShrink:0,
                        background:'rgba(255,255,255,0.04)', border:`1px solid ${C.border}`,
                        display:'flex', alignItems:'center', justifyContent:'center', fontSize:15,
                      }}>
                        {icon}
                      </div>
                      <div>
                        <div style={{ fontSize:12, fontWeight:700, color:'#fff', fontFamily:sans, marginBottom:3 }}>{title}</div>
                        <div style={{ fontSize:11, color:C.muted2, fontFamily:mono, lineHeight:1.6 }}>{desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA — strategy page */}
              <div style={{
                ...cardStyle,
                display:'flex', alignItems:'center',
                justifyContent:'space-between',
                gap:16, flexWrap:'wrap',
                background: `linear-gradient(135deg, rgba(0,229,160,0.06), rgba(61,142,240,0.06))`,
                border:`1px solid rgba(0,229,160,0.15)`,
              }}>
                <div style={{ flex:1, minWidth:200 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'#fff', fontFamily:sans, marginBottom:5 }}>
                    Explore while you wait
                  </div>
                  <div style={{ fontSize:12, color:C.muted2, fontFamily:mono, lineHeight:1.6 }}>
                    Check live strategy performance, historical PnL and how the algorithm selects strikes in real time.
                  </div>
                </div>
                <button
                  onClick={() => navigate('/strategy')}
                  style={{
                    padding:'11px 22px', flexShrink:0,
                    background:'transparent',
                    border:`1.5px solid ${C.green}`,
                    borderRadius:10, color:C.green,
                    fontSize:13, fontWeight:700, fontFamily:sans,
                    cursor:'pointer', whiteSpace:'nowrap',
                    display:'flex', alignItems:'center', gap:8,
                    transition:'background 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(0,229,160,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}
                >
                  View Strategy Performance →
                </button>
              </div>

              {/* refresh link */}
              <p style={{ textAlign:'center', fontSize:12, color:C.muted, fontFamily:mono, margin:0 }}>
                Already approved?{' '}
                <span
                  onClick={() => window.location.reload()}
                  style={{ color:C.green, cursor:'pointer', textDecoration:'underline' }}
                >
                  Refresh the page
                </span>
              </p>
            </div>
          )}

        </div>
      </div>
    </>
  );
}